import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Box,
  Typography,
  Divider,
  TextField,
  useMediaQuery,
  Button,
  IconButton,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  useOutletContext,
  useParams,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchTopProducts,
  fetchChildItems,
} from "../../store/reducers/productSlice";
import JsonEditor from "./JsonEditor";
import RequestSamples from "./RequestCodeExamples";
import Responses from "./Responses";
import VisitAdmin from "../../components/VisitAdmin";
import Cookies from "js-cookie";
import axios from "axios";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { vscDarkPlus } from "react-syntax-highlighter/dist/esm/styles/prism";
import Loader from "../../components/Loader";
import useAuthModalsStore from "../../store/reducers/useAuthModalsStore";
import Demo from "../../components/Demo";
import stripJsonComments from "strip-json-comments";

const SESSION_PARAMS_STORAGE_KEY = "developerToolEndpointParams";

const isPlainObject = (value) =>
  Object.prototype.toString.call(value) === "[object Object]";

const getEndpointStorageId = (endpoint, pathname) => {
  if (endpoint?.endpoint_id) {
    return `endpoint-${endpoint.endpoint_id}`;
  }

  if (endpoint?.request?.url?.raw) {
    return `url-${endpoint.request.url.raw}`;
  }

  return `path-${pathname}`;
};

const readStoredEndpointParams = () => {
  try {
    const saved = sessionStorage.getItem(SESSION_PARAMS_STORAGE_KEY);
    return saved ? JSON.parse(saved) : {};
  } catch (error) {
    console.error("Failed to read endpoint params from sessionStorage:", error);
    return {};
  }
};

const writeStoredEndpointParams = (endpointId, params) => {
  if (!endpointId) return;

  try {
    const saved = readStoredEndpointParams();
    sessionStorage.setItem(
      SESSION_PARAMS_STORAGE_KEY,
      JSON.stringify({
        ...saved,
        [endpointId]: {
          ...(saved[endpointId] || {}),
          ...params,
        },
      })
    );
  } catch (error) {
    console.error("Failed to write endpoint params to sessionStorage:", error);
  }
};

const mergeMatchingFields = (template, savedValue) => {
  if (savedValue === undefined) {
    return template;
  }

  if (Array.isArray(template)) {
    return template.map((item, index) =>
      mergeMatchingFields(item, Array.isArray(savedValue) ? savedValue[index] : undefined)
    );
  }

  if (isPlainObject(template)) {
    return Object.keys(template).reduce((acc, key) => {
      acc[key] = mergeMatchingFields(
        template[key],
        isPlainObject(savedValue) ? savedValue[key] : undefined
      );
      return acc;
    }, {});
  }

  return savedValue;
};

const mergeStoredFormData = (template, savedItems) => {
  if (!Array.isArray(template)) {
    return [];
  }

  const savedMap = new Map(
    Array.isArray(savedItems)
      ? savedItems.map((item) => [item?.key, item?.value])
      : []
  );

  return template.map((item) => {
    if (item?.type === "file") {
      return { ...item };
    }

    return savedMap.has(item?.key)
      ? { ...item, value: savedMap.get(item.key) }
      : { ...item };
  });
};

const sanitizeStoredBodyParams = (value, mode) => {
  if (mode === "formdata") {
    return Array.isArray(value)
      ? value.map((item) => ({
          key: item?.key,
          value: item?.type === "file" || item?.value instanceof File ? "" : item?.value ?? "",
        }))
      : [];
  }

  return value;
};

const Endpoint = () => {
  const { open } = useOutletContext();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const authRef = useRef(null);
  const paramRefs = useRef({});
  const { setLoginOpen, demoOpen, setDemoOpen } = useAuthModalsStore();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const token = Cookies.get("_d_token");
  const signature = Cookies.get("c_d_token");
  const { product, subproduct, subsubproduct } = useParams();
  const [jsonData, setJsonData] = useState({});
  const rawProducts = useSelector((state) => state.products.rawItems);
  const rawChildrenMap = useSelector((state) => state.products.rawChildrenMap);
  const [authToken, setAuthToken] = useState("");
  const jsonFieldRefs = useRef({});
  const jsonEditorRef = useRef();
  const [activeJsonTab, setActiveJsonTab] = useState("body");
  const switchJsonTab = (val) => setActiveJsonTab(val);
  const [apiResponse, setApiResponse] = useState(null);
  const [loader, setLoader] = useState(false);
  const [headers, setHeaders] = useState([]);
  const [rawJson, setRawJson] = useState("");

  const [pathParams, setPathParams] = useState({});

  const methodStyles = {
    GET: { color: "#388E3C", bg: "#E8F5E9" },
    POST: { color: "#36a168", bg: "#E3F2FD" },
    PUT: { color: "#F9A825", bg: "#FFF8E1" },
    DELETE: { color: "#D32F2F", bg: "#FFEBEE" },
    PATCH: { color: "#7B1FA2", bg: "#F3E5F5" },
    default: { color: "#1D1D1D", bg: "#F5F5F5" },
  };

  useEffect(() => {
    if (!rawProducts.length) {
      dispatch(fetchTopProducts());
    }
  }, [dispatch, rawProducts.length]);

  useEffect(() => {
    const loadChildren = async () => {
      const selectedProduct = rawProducts.find(
        (p) =>
          p.product_name.toLowerCase() ===
          decodeURIComponent(product).toLowerCase()
      );

      if (
        selectedProduct &&
        !rawChildrenMap[`product-${selectedProduct.product_id}`]
      ) {
        await dispatch(
          fetchChildItems({
            type: "product",
            id: selectedProduct.product_id,
          })
        );
      }
    };
    loadChildren();
  }, [dispatch, rawProducts, rawChildrenMap, product]);

  useEffect(() => {
    const loadEndpoints = async () => {
      const selectedProduct = rawProducts.find(
        (p) =>
          p.product_name.toLowerCase() ===
          decodeURIComponent(product).toLowerCase()
      );
      if (!selectedProduct) return;

      const tagList =
        rawChildrenMap[`product-${selectedProduct.product_id}`] || [];
      const selectedTag = tagList.find(
        (tag) =>
          tag.tag_name?.toLowerCase() ===
          decodeURIComponent(subproduct).toLowerCase()
      );
      if (!selectedTag) return;

      // 🔁 If endpoints for this tag aren't loaded, dispatch fetch
      if (!rawChildrenMap[`tag-${selectedTag.tag_id}`]) {
        await dispatch(
          fetchChildItems({
            type: "tag",
            id: selectedTag.tag_id,
            path: `/products/${selectedProduct.product_name}/${selectedTag.tag_name}`,
          })
        );
      }
    };

    loadEndpoints();
  }, [dispatch, rawProducts, rawChildrenMap, product, subproduct]);

  // Step 1: Get selected product
  const selectedProduct = rawProducts.find(
    (p) =>
      p.product_name.toLowerCase() === decodeURIComponent(product).toLowerCase()
  );

  // Step 2: Get selected tag
  const tagList =
    rawChildrenMap[`product-${selectedProduct?.product_id}`] || [];
  const selectedTag = tagList.find(
    (tag) =>
      tag.tag_name?.toLowerCase() ===
      decodeURIComponent(subproduct).toLowerCase()
  );

  // Step 3: Get selected endpoint
  const endpointList = rawChildrenMap[`tag-${selectedTag?.tag_id}`] || [];
  const selectedEndpoint = endpointList.find(
    (ep) =>
      ep.endpoint_name?.toLowerCase() ===
      decodeURIComponent(subsubproduct).toLowerCase()
  );
  const endpointStorageId = selectedEndpoint
    ? getEndpointStorageId(selectedEndpoint, location.pathname)
    : null;

  let method = "default",
    color = "#1D1D1D",
    bg = "#F5F5F5";
  if (selectedEndpoint) {
    method = selectedEndpoint?.request?.method?.toUpperCase() || "default";
    ({ color, bg } = methodStyles[method] || methodStyles.default);
  }

  // useEffect(() => {
  //   if (selectedEndpoint?.request?.body?.mode === "formdata") {
  //     const formdata = selectedEndpoint?.request?.body?.formdata || [];
  //     setJsonData(Array.isArray(formdata) ? formdata : []);
  //   } else {
  //     if(selectedEndpoint?.request?.body?.raw)
  //     {
  //       console.log(selectedEndpoint?.request?.body?.raw)
  //       let parsed = {};
  //       try {
  //         parsed = typeof selectedEndpoint.request.body.raw === "string"
  //           ? JSON.parse(selectedEndpoint.request.body.raw)
  //           : selectedEndpoint.request.body.raw;
  //       } catch (e) {
  //         console.error("Invalid JSON:", e);
  //       }
  //       setJsonData(parsed);
  //     }
  //   }
  // }, [selectedEndpoint]);

  useEffect(() => {
    if (!selectedEndpoint?.request?.body) {
      setJsonData({});
      return;
    }

    const { body } = selectedEndpoint.request;
    const savedParams = endpointStorageId
      ? readStoredEndpointParams()[endpointStorageId]
      : null;

    if (body.mode === "formdata") {
      const formDataTemplate = Array.isArray(body.formdata)
        ? body.formdata.map((item) => ({ ...item }))
        : [];
      setJsonData(mergeStoredFormData(formDataTemplate, savedParams?.bodyParams));
    } else if (body.mode === "raw") {
      setRawJson(body.raw || "");
      if (body.raw && body.raw.trim() !== "") {
        try {
          const clean = stripJsonComments(body.raw);
          const parsed = typeof clean === "string" ? JSON.parse(clean) : clean;
          setJsonData(mergeMatchingFields(parsed, savedParams?.bodyParams));
        } catch (e) {
          console.error("Invalid JSON:", e);
          setJsonData({});
        }
      } else {
        setJsonData({});
      }
    } else {
      setJsonData({});
    }
  }, [selectedEndpoint, endpointStorageId]);

  useEffect(() => {
    if (selectedEndpoint?.request?.header) {
      setHeaders(selectedEndpoint.request.header.map((h) => ({ ...h })));
    }
  }, [selectedEndpoint]);

  // Restore path params only for placeholders that exist on the selected API.
  const parsePath = (path) => {
    const regex = /<[^>]+>/g;
    const parts = [];
    let lastIndex = 0;

    path.replace(regex, (match, offset) => {
      if (lastIndex < offset) {
        parts.push({ type: "text", value: path.slice(lastIndex, offset) });
      }
      parts.push({ type: "param", value: match.slice(1, -1) });
      lastIndex = offset + match.length;
    });

    if (lastIndex < path.length) {
      parts.push({ type: "text", value: path.slice(lastIndex) });
    }

    return parts;
  };

  const rawUrl = selectedEndpoint?.request?.url?.raw || "";
  const rawPath = rawUrl ? decodeURIComponent(new URL(rawUrl).pathname) : "";
  const pathParts = useMemo(() => parsePath(rawPath), [rawPath]);

  useEffect(() => {
    if (!endpointStorageId) {
      setPathParams({});
      return;
    }

    const savedParams = readStoredEndpointParams()[endpointStorageId];
    const currentParamKeys = pathParts
      .filter((part) => part.type === "param")
      .map((part) => part.value);

    const restoredParams = currentParamKeys.reduce((acc, key) => {
      if (savedParams?.pathParams && savedParams.pathParams[key] !== undefined) {
        acc[key] = savedParams.pathParams[key];
      }
      return acc;
    }, {});

    setPathParams(restoredParams);
  }, [endpointStorageId, pathParts]);

  useEffect(() => {
    if (!endpointStorageId) return;

    writeStoredEndpointParams(endpointStorageId, {
      bodyMode: selectedEndpoint?.request?.body?.mode || "raw",
      bodyParams: sanitizeStoredBodyParams(
        jsonData,
        selectedEndpoint?.request?.body?.mode
      ),
    });
  }, [endpointStorageId, jsonData, selectedEndpoint]);

  useEffect(() => {
    if (!endpointStorageId) return;

    writeStoredEndpointParams(endpointStorageId, {
      pathParams,
    });
  }, [endpointStorageId, pathParams]);

  useEffect(() => {
    const saved = localStorage.getItem(`tryit:${location.pathname}`);
    if (saved) {
      try {
        setApiResponse(JSON.parse(saved));
      } catch {
        // ignore invalid JSON
      }
    }
  }, [location.pathname, selectedEndpoint]);

  if (!selectedProduct || !selectedTag || !selectedEndpoint) {
    return <Typography sx={{ p: 3 }}>Loading endpoint data...</Typography>;
  }
  console.log(selectedEndpoint);

  const handleTryIt = async () => {
    if (signature) {
      try {
        const method = selectedEndpoint?.request?.method?.toLowerCase();
        for (const part of pathParts) {
          if (part.type === "param" && !pathParams[part.value]?.trim()) {
            paramRefs.current[part.value]?.focus();
            return;
          }
        }

        const url = buildFinalUrl();
        console.log(url);
        if (!url) {
          alert("No endpoint URL found.");
          return;
        }

        if (selectedEndpoint?.request?.body?.raw) {
          try {
            // 🔹 Recursive validation
            const checkBlank = (obj, parentKey = "") => {
              for (let [k, v] of Object.entries(obj)) {
                const keyPath = parentKey ? `${parentKey}.${k}` : k;
                if (typeof v === "string" && v.trim() === "") {
                  switchJsonTab("params");
                  const field = jsonFieldRefs.current[keyPath];
                  if (field) {
                    field.scrollIntoView({
                      behavior: "smooth",
                      block: "center",
                    });
                    setTimeout(() => field.focus(), 300);
                  }
                  throw new Error(`Payload key "${keyPath}" is blank`);
                }
                if (v && typeof v === "object") checkBlank(v, keyPath);
              }
            };
            checkBlank(jsonData);
          } catch (err) {
            // alert(err.message);
            return;
          }
        }

        const headersObj = {};
        headers.forEach(({ key, value }) => {
          if (key && value !== undefined) {
            if (typeof value === "string" && value.trim() === "") {
              if (authRef.current) {
                authRef.current.scrollIntoView({
                  behavior: "smooth",
                  block: "center",
                });
                setTimeout(() => authRef.current.focus(), 300);
              }
              return;
            }
            headersObj[key] = value;
          }
        });

        let dataToSend;
        if (selectedEndpoint?.request?.body?.mode === "formdata") {
          dataToSend = new FormData();
          jsonData.forEach((item) => {
            if (item.type === "file" && item.value instanceof File) {
              dataToSend.append(item.key, item.value, item.value.name);
            } else {
              dataToSend.append(item.key, item.value);
            }
          });
        } else {
          dataToSend = jsonData;
        }

        const options = {
          method,
          url,
          headers: headersObj,
          data: dataToSend,
        };

        setLoader(true);

        const response = await axios(options);
        console.log("✅ API Response:", response.data);
        const responseObj = {
          type: "success",
          data: response.data,
          timestamp: Date.now(),
        };

        localStorage.setItem(
          `tryit:${location.pathname}`,
          JSON.stringify(responseObj)
        );

        setApiResponse(responseObj);
        window.dispatchEvent(new Event("developer-tool:tryit-success"));
        setLoader(false);
      } catch (error) {
        setLoader(false);
        console.error(
          "❌ Request failed:",
          error.response?.data || error.message
        );
        const responseObj = {
          type: "error",
          data: error.response?.data || { message: error.message },
        };

        localStorage.setItem(
          `tryit:${location.pathname}`,
          JSON.stringify(responseObj)
        );

        setApiResponse(responseObj);
        // alert(`Error: ${error.response?.data?.message || error.message}`);
      }
    } else {
      // navigate("/login", { state: { from: location } });
      setLoginOpen(true);
    }
  };

  const handleViewInPostman = () => {
    if (signature) {
      // User is logged in, open Postman link
      window.open(selectedTag.collection_url, "_blank");
    } else {
      // User is not logged in, open login modal
      setLoginOpen(true);
    }
  };
  const JsonViewer = ({ data, level = 0 }) => {
    const [copiedKey, setCopiedKey] = useState(null);

    const copyValue = (val, key) => {
      navigator.clipboard.writeText(String(val));
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1500); // reset after 1.5s
    };

    if (typeof data !== "object" || data === null) {
      return (
        <span
          style={{ color: "#9cdcfe", cursor: "pointer" }}
          onClick={() => copyValue(data, String(data))}
          title="Click to copy"
        >
          {JSON.stringify(data)}{" "}
          {copiedKey === String(data) && (
            <span style={{ color: "lime", fontSize: "12px", marginLeft: 4 }}>
              (Copied!)
            </span>
          )}
        </span>
      );
    }

    return (
      <div style={{ paddingLeft: level * 2 }}>
        {"{"}
        {Object.entries(data).map(([key, value], index, arr) => (
          <div key={key} style={{ marginLeft: 2 }}>
            <span style={{ color: "#ce9178" }}>"{key}"</span>
            <span style={{ color: "#d4d4d4" }}>: </span>
            <JsonViewer data={value} level={level + 1} />
            {index < arr.length - 1 ? "," : ""}
          </div>
        ))}
        {"}"}
      </div>
    );
  };

  const handleParamChange = (key, val) => {
    setPathParams((prev) => ({ ...prev, [key]: val }));
  };

  // Build final URL with replaced params
  const buildFinalUrl = () => {
    let url = rawUrl;
    Object.entries(pathParams).forEach(([key, val]) => {
      url = url.replace(`<${key}>`, val || `<${key}>`);
    });
    return url;
  };

  return (
    <>
      <Box
        sx={{
          flexGrow: 1,
          p: isMobile ? 2 : 3,
          width: isMobile
            ? "100%"
            : open
            ? "calc(100% - 280px)"
            : "calc(100% - 100px)",
          marginLeft: isMobile ? "0" : open ? "280px" : "100px",
          transition: "margin 0.3s ease",
          height: "100vh",
          display: "flex",
          background: "#f9f9f9",
        }}
      >
        <Box
          mt={10}
          style={{
            textAlign: "center",
            width: "100%",
            marginLeft: open ? "" : "1em",
          }}
        >
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 400,
              lineHeight: "20px",
              color: "#6C6C6C",
              textAlign: "left",
              mb: "15px",
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            {selectedProduct?.product_name}
            <svg
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M6.75 4.5L10.5429 8.29289C10.8762 8.62623 11.0429 8.79289 11.0429 9C11.0429 9.20711 10.8762 9.37377 10.5429 9.70711L6.75 13.5"
                stroke="#141B34"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {selectedTag?.tag_name}
          </Typography>
          <Box
            sx={{
              borderRadius: "12px",
              background: "#fff",
              height: "calc(100% - 28px)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: isMobile ? "15px 10px" : "25px",
              }}
            >
              <Typography
                sx={{
                  fontSize: "20px",
                  fontWeight: 500,
                  lineHeight: "20px",
                  color: "#1D1D1D",
                  textAlign: "left",
                }}
              >
                {selectedEndpoint?.endpoint_name}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "10px",
                }}
              >
                {!token &&
                  selectedProduct?.sso_path &&
                  selectedProduct?.sso_path?.toLowerCase() !== "none" && (
                    <>
                      <VisitAdmin
                        productData={selectedProduct}
                        header={false}
                        selectedSubItem={selectedTag}
                      />
                    </>
                  )}
                {selectedTag?.demo_url && !isMobileOrTablet && (
                  <Button
                    variant="contained"
                    sx={{
                      width: "154px",
                      height: 42,
                      color: "#185CB7",
                      background: "#ffffff",
                      textTransform: "none",
                      fontSize: "16px",
                      borderRadius: "10px",
                      border: "1px solid #185CB7",
                      boxShadow: "none",
                      fontWeight: "600",
                      "&:hover": {
                        background: "#ffffff",
                        color: "#185CB7",
                        borderColor: "#185CB7",
                      },
                      gap: "13px",
                      whiteSpace: "nowrap",
                    }}
                    onClick={() => setDemoOpen(true)}
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g clip-path="url(#clip0_3000_11689)">
                        <circle
                          cx="9.99935"
                          cy="9.99935"
                          r="8.33333"
                          stroke="#185CB7"
                          stroke-width="1.5"
                        />
                        <path
                          d="M12.8447 9.11688C13.4962 9.50151 13.4962 10.4972 12.8447 10.8818L8.91124 13.2042C8.27809 13.578 7.5 13.0915 7.5 12.3217L7.5 7.67698C7.5 6.90724 8.27809 6.42069 8.91124 6.79451L12.8447 9.11688Z"
                          fill="#185CB7"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_3000_11689">
                          <rect width="20" height="20" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                    Play Demo
                  </Button>
                )}
                {selectedTag?.collection_url && !isMobileOrTablet && (
                  <Button
                    variant="contained"
                    sx={{
                      minWidth: "154px",
                      height: 42,
                      color: "#185CB7",
                      background: "#ffffff",
                      textTransform: "none",
                      fontSize: "16px",
                      borderRadius: "10px",
                      border: "1px solid #185CB7",
                      boxShadow: "none",
                      fontWeight: "600",
                      "&:hover": {
                        background: "#ffffff",
                        color: "#185CB7",
                        borderColor: "#185CB7",
                      },
                      gap: "13px",
                      whiteSpace: "nowrap",
                    }}
                    onClick={handleViewInPostman}
                  >
                    <svg
                      width="22"
                      height="14"
                      viewBox="0 0 22 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M17 3L19.4531 5.11438C20.4844 6.00327 21 6.44772 21 7C21 7.55228 20.4844 7.99673 19.4531 8.88562L17 11"
                        stroke="#185CB7"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M5 3L2.54692 5.11438C1.51564 6.00327 1 6.44772 1 7C1 7.55228 1.51564 7.99673 2.54692 8.88562L5 11"
                        stroke="#185CB7"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M13 1L9 13"
                        stroke="#185CB7"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                    View in Postman
                  </Button>
                )}
              </Box>
            </Box>
            <Divider />
            <Box sx={{ textAlign: "left", height: "calc(100% - 100px)" }}>
              <Box sx={{ display: "flex", gap: 2, height: "calc(100% - 0px)" }}>
                <Box
                  sx={{
                    flex: 7,
                    overflowY: "auto",
                    height: "calc(100% - 0px)",
                    p: isMobile ? "15px 10px" : "15px 30px",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "13px",
                      fontWeight: 400,
                      lineHeight: "20px",
                      color: "#636262",
                    }}
                  >
                    {selectedEndpoint?.description}
                  </Typography>

                  <Box
                    sx={{
                      height: 50,
                      display: "flex",
                      alignItems: "center",
                      gap: 3,
                      background:
                        "linear-gradient(154deg, rgba(24, 92, 183, 0.05) 0%, rgba(168, 21, 40, 0.05) 99.51%)",
                      p: "4px 4px 4px 0",
                      borderRadius: "12px",
                      mt: 1,
                      position: "relative",
                    }}
                  >
                    <Box
                      sx={{
                        height: 50,
                        background:
                          "linear-gradient(118.21deg, #E8F1FF 18.13%, #FFECF1 97.97%)",
                        pl: "18px",
                        pr: "18px",
                        borderRadius: "12px 0 0 12px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRight: "1px solid transparent",
                        borderImage:
                          "linear-gradient(90deg, #FFD3DF 0%, #EFD8DE 100%) 1",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "16px",
                          fontWeight: 600,
                          lineHeight: "20px",
                          background:
                            "linear-gradient(90deg, #2257AD 0%, #B01121 100%)",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                          backgroundClip: "text",
                          color: "transparent",
                        }}
                      >
                        {method}
                      </Typography>
                    </Box>
                    {/* <Typography sx={{ fontSize: '16px', fontWeight: 500, lineHeight: '20px', color: "#1D1D1D", wordBreak: "break-all", }}>
                    {'{BASE_URL}'}{decodeURIComponent(new URL(selectedEndpoint?.request?.url?.raw).pathname)}
                  </Typography> */}
                    <Typography
                      sx={{
                        fontSize: "16px",
                        fontWeight: 500,
                        lineHeight: "20px",
                        color: "#1D1D1D",
                        wordBreak: "break-all",
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      {"{BASE_URL}"}
                      {pathParts.map((part, idx) =>
                        part.type === "text" ? (
                          <span key={idx}>{part.value}</span>
                        ) : (
                          <TextField
                            key={idx}
                            placeholder={part.value}
                            value={pathParams[part.value] || ""}
                            onChange={(e) =>
                              handleParamChange(part.value, e.target.value)
                            }
                            inputRef={(el) =>
                              (paramRefs.current[part.value] = el)
                            }
                            sx={{
                              fontSize: "16px",
                              "& .MuiInputBase-input": {
                                height: "28px",
                                padding: "0px 8px",
                              },
                            }}
                          />
                        )
                      )}
                    </Typography>

                    <IconButton
                      onClick={handleTryIt}
                      sx={{
                        height: 42,
                        background: "#185CB7",
                        borderRadius: "10px",
                        p: "15px 25px",
                        border: "1px solid #185CB7",
                        "&:hover": { background: "#185CB7" },
                        position: "absolute",
                        right: 5,
                        color: "#FFFFFF",
                        fontSize: "16px",
                        fontWeight: 600,
                      }}
                    >
                      Try IT !
                    </IconButton>
                  </Box>

                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 400,
                      lineHeight: "20px",
                      color: "#4b5563",
                      mt: 1,
                    }}
                  >
                    Note : Copy the <strong>CURL</strong> only after filling the
                    input parameters
                  </Typography>
                  <Divider sx={{ mt: 3 }} />
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 3,
                      mt: 3,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "18px",
                        fontWeight: 500,
                        lineHeight: "20px",
                        color: "#1D1D1D",
                      }}
                    >
                      Create Request for {selectedEndpoint?.endpoint_name}
                    </Typography>

                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: 400,
                          lineHeight: "20px",
                          color: "#1D1D1D",
                        }}
                      >
                        API Server :{" "}
                        <span style={{ color: "#4b5563" }}>
                          {new URL(selectedEndpoint?.request?.url?.raw).origin}
                        </span>
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: 400,
                          lineHeight: "20px",
                          color: "#1D1D1D",
                        }}
                      >
                        Raw URL :{" "}
                        <span
                          style={{ color: "#4b5563", wordBreak: "break-all" }}
                        >
                          {selectedEndpoint?.request?.url?.raw}
                        </span>
                      </Typography>
                    </Box>

                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      {jsonData &&
                        ((typeof jsonData === "object" &&
                          jsonData !== null &&
                          Object.keys(jsonData).length > 0) ||
                          (Array.isArray(jsonData) && jsonData.length > 0) ||
                          (typeof jsonData === "string" &&
                            jsonData.trim() !== "")) && (
                          <>
                            <Typography
                              sx={{
                                fontSize: "16px",
                                fontWeight: 500,
                                lineHeight: "20px",
                                color: "#1D1D1D",
                                mt: 1,
                              }}
                            >
                              Request Body
                            </Typography>

                            <JsonEditor
                              selectedEndpoint={selectedEndpoint}
                              jsonData={jsonData}
                              rawJson={rawJson}
                              setJsonData={setJsonData}
                              isMobile={isMobile}
                              jsonFieldRefs={jsonFieldRefs}
                              switchTab={switchJsonTab}
                              activeTab={activeJsonTab}
                            />
                          </>
                        )}

                      <Typography
                        sx={{
                          fontSize: "16px",
                          fontWeight: 500,
                          lineHeight: "20px",
                          color: "#1D1D1D",
                          mt: 2,
                          mb: 2,
                        }}
                      >
                        Request Header
                      </Typography>
                      {selectedEndpoint?.request?.header?.length > 0 &&
                        headers.map((header, index) => (
                          <Box
                            key={index}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mb: 1,
                            }}
                          >
                            <Typography
                              key={header.key}
                              sx={{
                                fontSize: "16px",
                                fontWeight: 500,
                                lineHeight: "20px",
                                color: "#1D1D1D",
                                display: "flex",
                                gap: 2,
                                ml: 2,
                              }}
                            >
                              <span style={{ whiteSpace: "nowrap" }}>
                                {header.key}{" "}
                                <span
                                  style={{ color: "#f06560", fontSize: "10px" }}
                                >
                                  (*required)
                                </span>
                                :
                              </span>
                            </Typography>

                            <TextField
                              inputRef={authRef}
                              size="small"
                              placeholder={`Enter ${header.key} value`}
                              value={header.value}
                              onChange={(e) => {
                                let newValue = e.target.value;
                                if (
                                  header.key.toLowerCase() ===
                                    "authorization" &&
                                  header?.authType?.toLowerCase() === "bearer"
                                ) {
                                  if (
                                    newValue &&
                                    !newValue
                                      .toLowerCase()
                                      .startsWith("bearer ")
                                  ) {
                                    newValue = `Bearer ${newValue}`;
                                  }
                                }

                                const newHeaders = [...headers];
                                newHeaders[index].value = newValue;
                                setHeaders(newHeaders);
                              }}
                              sx={{
                                width: "100%",
                                backgroundColor: "#fff",
                                borderRadius: "6px",
                                flex: 1,
                                "& .MuiOutlinedInput-root": {
                                  height: 30,
                                  fontSize: "16px",
                                  fontWeight: 500,
                                  borderRadius: "6px",
                                  backgroundColor: "#fff",
                                  "& fieldset": {
                                    borderColor: "#DFDFDF",
                                    borderWidth: "1px",
                                  },
                                  "&:hover fieldset": {
                                    borderColor: "#DFDFDF",
                                    borderWidth: "1px",
                                  },
                                  "&.Mui-focused fieldset": {
                                    borderColor: "#DFDFDF",
                                    borderWidth: "1px",
                                  },
                                },
                              }}
                              InputProps={{
                                sx: {
                                  borderRadius: "6px",
                                  "& input::-ms-reveal, & input::-ms-clear": {
                                    display: "none",
                                  },
                                },
                              }}
                            />

                            {header?.authType === "Basic Auth" && (
                              <>
                                <TextField
                                  inputRef={authRef}
                                  size="small"
                                  placeholder="Username"
                                  value={header.username || ""}
                                  onChange={(e) => {
                                    const newHeaders = [...headers];
                                    newHeaders[index].username = e.target.value;
                                    newHeaders[index].value = btoa(
                                      `${newHeaders[index].username || ""}:${
                                        newHeaders[index].password || ""
                                      }`
                                    );
                                    setHeaders(newHeaders);
                                  }}
                                  sx={{
                                    width: "100%",
                                    backgroundColor: "#fff",
                                    borderRadius: "6px",
                                    flex: 1,
                                    "& .MuiOutlinedInput-root": {
                                      height: 30,
                                      fontSize: "16px",
                                      fontWeight: 500,
                                      borderRadius: "6px",
                                      backgroundColor: "#fff",
                                      "& fieldset": {
                                        borderColor: "#DFDFDF",
                                        borderWidth: "1px",
                                      },
                                      "&:hover fieldset": {
                                        borderColor: "#DFDFDF",
                                        borderWidth: "1px",
                                      },
                                      "&.Mui-focused fieldset": {
                                        borderColor: "#DFDFDF",
                                        borderWidth: "1px",
                                      },
                                    },
                                  }}
                                  InputProps={{
                                    sx: {
                                      borderRadius: "6px",
                                      "& input::-ms-reveal, & input::-ms-clear":
                                        {
                                          display: "none",
                                        },
                                    },
                                  }}
                                />
                                <TextField
                                  inputRef={authRef}
                                  size="small"
                                  placeholder="Password"
                                  type="password"
                                  value={header.password || ""}
                                  onChange={(e) => {
                                    const newHeaders = [...headers];
                                    newHeaders[index].password = e.target.value;
                                    newHeaders[index].value = btoa(
                                      `${newHeaders[index].username || ""}:${
                                        newHeaders[index].password || ""
                                      }`
                                    );
                                    setHeaders(newHeaders);
                                  }}
                                  sx={{
                                    width: "100%",
                                    backgroundColor: "#fff",
                                    borderRadius: "6px",
                                    flex: 1,
                                    "& .MuiOutlinedInput-root": {
                                      height: 30,
                                      fontSize: "16px",
                                      fontWeight: 500,
                                      borderRadius: "6px",
                                      backgroundColor: "#fff",
                                      "& fieldset": {
                                        borderColor: "#DFDFDF",
                                        borderWidth: "1px",
                                      },
                                      "&:hover fieldset": {
                                        borderColor: "#DFDFDF",
                                        borderWidth: "1px",
                                      },
                                      "&.Mui-focused fieldset": {
                                        borderColor: "#DFDFDF",
                                        borderWidth: "1px",
                                      },
                                    },
                                  }}
                                  InputProps={{
                                    sx: {
                                      borderRadius: "6px",
                                      "& input::-ms-reveal, & input::-ms-clear":
                                        {
                                          display: "none",
                                        },
                                    },
                                  }}
                                />
                              </>
                            )}
                          </Box>
                        ))}

                      {selectedEndpoint?.request?.code_samples && (
                        <RequestSamples
                          selectedEndpoint={selectedEndpoint?.request}
                        />
                      )}

                      {selectedEndpoint?.response.length > 0 && (
                        <>
                          <Typography
                            sx={{
                              fontSize: "16px",
                              fontWeight: 500,
                              lineHeight: "20px",
                              color: "#1D1D1D",
                              mt: 1,
                            }}
                          >
                            Response
                          </Typography>
                          <Responses
                            selectedEndpoint={selectedEndpoint?.response}
                          />
                        </>
                      )}
                    </Box>
                  </Box>
                </Box>
                {apiResponse && (
                  <Box
                    sx={{
                      flex: 3,
                      overflowY: "auto",
                      height: "calc(100% - 0px)",
                      p: isMobile ? "15px 10px" : "15px 30px",
                    }}
                  >
                    <Typography
                      sx={{ fontSize: "16px", fontWeight: 500, mb: 1 }}
                    >
                      API Response (
                      {apiResponse.type === "success"
                        ? "✅ Success"
                        : "❌ Error"}
                      )
                    </Typography>
                    {console.log(apiResponse)}
                    <Box
                      sx={{
                        background: "#1e1e1e",
                        borderRadius: "8px",
                        p: 2,
                        fontFamily: "monospace",
                        fontSize: "14px",
                        color: "#d4d4d4",
                        overflowX: "auto",
                      }}
                    >
                      <JsonViewer data={apiResponse.data} />
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
      {loader && <Loader />}
      {demoOpen && (
        <Demo
          demoOpen={demoOpen}
          setDemoOpen={setDemoOpen}
          selectedSubItem={selectedTag}
        />
      )}
    </>
  );
};

export default Endpoint;
